import { useMemo, useState } from 'react';
import { Trans } from '@lingui/react/macro';
import { QRCodeSVG } from 'qrcode.react';

import config from '@/config';
import Box from '@/design_system/Box';
import Button from '@/design_system/Button';
import Stack from '@/design_system/Stack';
import Paragraph from '@/design_system/Typography/Paragraph';
import IconArrow from '@/icons/Arrow.svg';
import IconPlace from '@/icons/Place.svg';
import {
  BrandRequestMainContent,
  BrandRequestMainFooter,
} from '@/layouts/Brand/BrandRequestLayout/BrandRequestLayout';
import { useSetValidationChoice } from '@/models/article';
import { RequestWithRelations, useCompleteValidation } from '@/models/request';
import { ClientArticlesList } from '@/routes/Brand/Requests/Request/components/shared/ClientArticlesList';
import { DropOffStoresDialog } from '@/routes/Brand/Requests/Request/components/shared/DropOffStoresDialog';
import { PickupPointInfo } from '@/routes/Brand/Requests/Request/components/shared/PickupPointInfo/PickupPointInfo';
import { useClientToken } from '@/services/auth';
import { createBEMClasses } from '@/utils/classname';

import {
  Choice,
  Choices,
} from '../../../shared/ClientArticlesList/components/ArticleNameWithChoice';
import ClientStepper from '../../../shared/ClientStepper';
import { StepMessage } from '../../../shared/StepMessage';

import './Validation.css';

const { block, element } = createBEMClasses('client-request-validation');

const Validation = ({ request }: { request: RequestWithRelations }) => {
  const {
    mutateAsync: completeValidation,
    isPending: isPendingCompleteValidation,
    isSuccess: isSuccessCompleteValidation,
  } = useCompleteValidation(request.id);

  const articlesInValidation = request.articles.filter(
    (article) => article.step?.step === 'validation'
  );

  const requireShippingChoice =
    !request.store &&
    !!request.client &&
    articlesInValidation[0].step?.step === 'validation' &&
    !!articlesInValidation[0].step.config.requireShippingChoice;

  const choicesFromRequest = useMemo(() => {
    return request.articles
      .filter((article) => article.step?.step === 'validation')
      .reduce((acc, curr) => {
        acc[curr.id] = {
          value: curr.quoteAcceptedAt ? 'accepted' : 'refused',
          reason: curr.cancellationDetail?.reason ?? null,
          otherReason: curr.cancellationDetail?.otherReason ?? null,
        };

        return acc;
      }, {} as Choices);
  }, [request.articles]);

  const [choices, setChoices] = useState(choicesFromRequest);

  const { mutateAsync: setValidationChoice } = useSetValidationChoice({ requestId: request.id });

  const onChangeChoice = (articleId: string, choice: Choice) => {
    setChoices({
      ...choices,
      [articleId]: choice,
    });

    if (choice.value) {
      setValidationChoice({
        articleId,
        choice: { ...choice, value: choice.value },
      }).catch(() => setChoices(choicesFromRequest));
    }
  };

  const [showFormError, setShowFormError] = useState(false);

  const reasonMissing = articlesInValidation.some(
    (article) =>
      !!article.quoteRefusedAt &&
      (!article.cancellationDetail?.reason ||
        (article.cancellationDetail?.reason === 'other' &&
          !article.cancellationDetail?.otherReason))
  );

  const shippingChoiceMissing =
    requireShippingChoice &&
    articlesInValidation.some((article) => article.quoteAcceptedAt) &&
    !request.client!.shippingChoice;

  const [error, setError] = useState<string | null>(null);
  const isValidating = articlesInValidation.some((article) => !article.validationCompletedAt);

  const showShippingOptions =
    isValidating &&
    requireShippingChoice &&
    articlesInValidation.some((article) => article.quoteAcceptedAt);

  const hasValidatedAndIsInStoreDropoff =
    !isValidating &&
    articlesInValidation.some(
      (article) => !!article.quoteAcceptedAt && !!article.validationCompletedAt
    ) &&
    requireShippingChoice &&
    request.client?.shippingChoice === 'store';

  function onSubmit() {
    if (reasonMissing || shippingChoiceMissing) {
      setShowFormError(true);

      return;
    }

    setError(null);

    completeValidation().catch((error) => {
      setError(error.message as string);
    });
  }

  return (
    <>
      <BrandRequestMainContent>
        <Stack gap="1rem" className={block()}>
          <ClientStepper request={request} />
          {isValidating && <ValidationHeader />}
          {hasValidatedAndIsInStoreDropoff && <StoreDropoffHeader request={request} />}
          <PickupPointInfo request={request} />
          <ClientArticlesList
            request={request}
            showPrice
            showArticleComment
            choices={isValidating ? choices : undefined}
            setChoice={isValidating ? onChangeChoice : undefined}
            showChoicesError={isValidating ? showFormError : undefined}
            showShippingOptions={showShippingOptions}
            acceptedArticlesOnly={hasValidatedAndIsInStoreDropoff}
          />
        </Stack>
      </BrandRequestMainContent>

      {isValidating && (
        <BrandRequestMainFooter>
          <Stack gap="0.5rem" style={{ flex: 1 }}>
            {((showFormError && (reasonMissing || shippingChoiceMissing)) || error) && (
              <Paragraph weight="medium" color="danger" className="text-center">
                {reasonMissing ? (
                  <Trans id="client.request.validation.error.reason-missing">
                    Please select a reason for each refusal
                  </Trans>
                ) : shippingChoiceMissing ? (
                  <Trans id="client.request.validation.error.shipping-choice-missing">
                    Please select a shipping option
                  </Trans>
                ) : (
                  error
                )}
              </Paragraph>
            )}
            <Button
              variant="brand"
              size="large"
              onPress={onSubmit}
              isLoading={isPendingCompleteValidation || isSuccessCompleteValidation}
              disabled={isPendingCompleteValidation || isSuccessCompleteValidation}
            >
              <Trans id="client.request.validation.continue">Confirm & continue</Trans>
              <IconArrow right />
            </Button>
          </Stack>
        </BrandRequestMainFooter>
      )}
    </>
  );
};

const ValidationHeader = () => {
  return (
    <StepMessage
      message={
        <Trans id="client.request.validation.entry.description">
          Our experts have issued a quote. Please review it:
        </Trans>
      }
    />
  );
};

const StoreDropoffHeader = ({ request }: { request: RequestWithRelations }) => {
  const clientToken = useClientToken();
  const [isOpenStoreListModal, setIsOpenStoreListModal] = useState(false);

  return (
    <>
      <Stack gap="1rem">
        <StepMessage
          message={
            <Trans id="client.request.validation.pending-store-dropoff.title">
              Please bring your item to the closest authorized {request.organization.name} store.
            </Trans>
          }
        />

        <Box padding="16px">
          <Stack row gap="1rem" alignItems="center">
            <QRCodeSVG
              className={element('shipment-options__qr-code')}
              size={96}
              value={`${config.appUrl}/requests/claim?id=${request.id}&clientToken=${clientToken}`}
            />
            <Stack gap="8px" alignItems="flex-start">
              <Paragraph color="secondary">
                <Trans id="client.request.validation.pending-store-dropoff.description">
                  Drop your item at the nearest authorized {request.organization.name} store.
                  <br />
                  Show this QR code to the store manager to them grant access to the request.
                </Trans>
              </Paragraph>
              <Button variant="link" onPress={() => setIsOpenStoreListModal(true)}>
                <IconPlace start />
                <Trans id="client.request.validation.pending-store-dropoff.action">
                  See authorized stores
                </Trans>
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Stack>

      <DropOffStoresDialog isOpen={isOpenStoreListModal} onOpenChange={setIsOpenStoreListModal} />
    </>
  );
};

export default Validation;
